import React from "react";
import { Handle, Position, type NodeProps } from "reactflow";
import { FiEye, FiHardDrive, FiDatabase } from "react-icons/fi";

import { SchemaTableName } from "./SchemaTableName";
import type { LinkNodeData } from "./types";
import { isVirtualizedConstruct } from "../model/constructs";
import { isInternalRegisteredAsset } from "./internalAssetStyling";
import { NotGeneratedBadge, notGeneratedNodeClasses } from "./notGeneratedStyling";

export function LinkNode({ data, selected }: NodeProps<LinkNodeData>): JSX.Element {
  const { entity, onSelect } = data;
  const targetHandleClass = "!tw-h-3 !tw-w-3 !tw-border !tw-border-emerald-300 !tw-bg-white";
  const sourceHandleClass = "!tw-h-3 !tw-w-3 !tw-border-2 !tw-border-white !tw-bg-emerald-500";
  const constructType = entity.record.datavault_construct_type ?? "";
  const isVirtualized = isVirtualizedConstruct(constructType);
  const isInternal = isInternalRegisteredAsset(entity.record.data_source_type);
  const StatusIcon = isInternal ? FiDatabase : isVirtualized ? FiEye : FiHardDrive;
  const statusLabel = isInternal ? "Internal" : isVirtualized ? "Virtualized" : "Persisted";

  const baseClasses =
    "tw-group tw-relative tw-min-w-[220px] tw-cursor-pointer tw-rounded-3xl tw-border tw-bg-gradient-to-br tw-from-white tw-via-emerald-50 tw-to-emerald-100 tw-p-5 tw-text-left tw-shadow-lg tw-transition-transform tw-duration-150 hover:tw--translate-y-1";
  const selectionClasses = selected
    ? "tw-border-[4px] tw-border-emerald-900 tw-ring-[5px] tw-ring-emerald-500/70 tw-shadow-[0_0_20px_rgba(6,95,70,0.45)]"
    : "tw-border-emerald-200 tw-ring-1 tw-ring-emerald-200/60";
  return (
    <div
      onClick={(event) => {
        if (event.shiftKey || event.metaKey || event.ctrlKey) {
          return;
        }
        onSelect(entity.id);
      }}
      className={`${baseClasses} ${selectionClasses} ${notGeneratedNodeClasses(entity.record)}`}
    >
      <NotGeneratedBadge record={entity.record} />
      <Handle
        type="target"
        id="link-target-top"
        position={Position.Top}
        className={targetHandleClass}
      />
      <Handle
        type="source"
        id="link-source-top"
        position={Position.Top}
        className={sourceHandleClass}
      />
      <Handle
        type="source"
        id="link-source-bottom"
        position={Position.Bottom}
        className={sourceHandleClass}
      />
      <Handle
        type="source"
        id="link-source-left"
        position={Position.Left}
        className={sourceHandleClass}
      />
      <Handle
        type="source"
        id="link-source-right"
        position={Position.Right}
        className={sourceHandleClass}
      />
      <div className="tw-absolute tw-right-4 tw-top-4 tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-border tw-border-white/60 tw-bg-white/90 tw-px-2 tw-py-1 tw-text-[10px] tw-font-semibold tw-text-slate-600">
        <StatusIcon className={`tw-h-3 tw-w-3 ${isInternal || isVirtualized ? "tw-text-[#03234a]" : ""}`} />
        <span className={isInternal || isVirtualized ? "tw-text-[#03234a]" : "tw-text-slate-500"}>
          {statusLabel}
        </span>
      </div>
      <div className="tw-flex tw-items-center tw-gap-3">
        <div className="tw-flex tw-h-11 tw-w-11 tw-items-center tw-justify-center tw-rounded-2xl tw-bg-emerald-500/90 tw-text-white tw-shadow-lg">
          <span className="tw-text-base tw-font-semibold">L</span>
        </div>
        <div>
          <p className="tw-text-sm tw-font-semibold tw-text-slate-800">Link</p>
          <p className="tw-text-[11px] tw-uppercase tw-tracking-wide tw-text-emerald-600">
            Relationships between hubs
          </p>
        </div>
      </div>
      <div className="tw-mt-3">
        <SchemaTableName
          schema={entity.record.target_schema}
          table={entity.record.target_table}
          className="tw-block tw-font-mono tw-text-[13px] tw-font-semibold tw-leading-5 tw-text-slate-900 tw-break-words"
        />
      </div>
    </div>
  );
}
